// ════════════════════════════════════════════════════════════════════
// ieSocial.js — THE SOCIAL LAYER OF THE ILLEGAL EMPIRE
// ════════════════════════════════════════════════════════════════════
// IE_SOCIAL_VERSION 1.0 — minted 2026-08-21.
//
// MASTER COPY: gamemaster/engine/ieSocial.js
// This file is vendored BYTE-IDENTICAL into every terror-tcg app's src/.
// Fix it here first, then re-vendor. A drifted copy is a bug, not a fork.
//
// ────────────────────────────────────────────────────────────────────
// WHAT THIS IS
// ────────────────────────────────────────────────────────────────────
// IE_CANON_PRIME L84: "Players have one identity across every IE game.
// One login. One profile. One wallet. One friends list."
//
// The friends list has been "designed, not shipped" since it was written
// down (IE_CANON_PRIME L3247). This is the shipping of it. It is one
// list, stored once, on the shared soul — so a friend you add while
// rolling dice in Snake Eyes is a friend standing next to you on the
// Pier, and the invite that pulls them into Terror is the same invite
// either way.
//
// ────────────────────────────────────────────────────────────────────
// THE THREE NODES
// ────────────────────────────────────────────────────────────────────
//
//   profiles/{uid}/friends/{friendUid}
//       { status, name, addedAt }
//       status ∈ "out" | "in" | "accepted" | "blocked"
//       THE EDGE. Canon's path, unchanged. Lives on the soul, so it
//       follows the player into every title for free.
//
//   presence/{uid}
//       { name, game, zone, url, ts }
//       WHERE THEY ARE RIGHT NOW. Top-level and game-agnostic on
//       purpose: a per-zone presence node can only answer "who is in
//       this room", and the whole point of a friends list is answering
//       "where in the Empire is this person" from anywhere else in it.
//       Cleared by onDisconnect() — see THE DISCONNECT LAW below.
//
//   invites/{targetUid}/{fromUid}
//       { fromUid, fromName, gameId, label, url, partyId, note, ts }
//       COME OVER HERE. Keyed by sender so two friends inviting you at
//       once do not overwrite each other — the flaw in the older
//       single-slot partyInvites/{uid} node, which this supersedes for
//       cross-game travel while PartySystem keeps owning party seats.
//
//   markIndex/{codeKey} = uid
//       THE PHONE BOOK. markCode() is derived from the uid and
//       therefore one-way; adding a friend by their code needs a way
//       back. Create-only, self-written, holds nothing but a uid.
//
// ────────────────────────────────────────────────────────────────────
// THE MUTUAL-ADD SEAL  ⚠ read before touching rules.json
// ────────────────────────────────────────────────────────────────────
// A friendship is two edges and one of them lives on someone else's
// profile — which Law 8 (auth.uid IS THE ONLY IDENTITY) appears to
// forbid outright. It does not, and the distinction is the whole
// design:
//
//   You may write exactly ONE node under another player's profile:
//   the friend edge whose KEY IS YOUR OWN UID.
//
//   profiles/$uid/friends/$friendUid
//     ".write": "auth != null && (auth.uid === $uid || auth.uid === $friendUid)"
//
// You can put yourself on someone's list. You can never touch any
// other row of it, any other field of their soul, or their currency.
// The blast radius of a compromised client is "sent an unwanted friend
// request", which is what the block status is for. This needs no Cloud
// Function, which matters — ie-functions/ is written but undeployed
// (needs Blaze), and a friend system that waits on billing is a friend
// system that does not ship.
//
// ────────────────────────────────────────────────────────────────────
// THE DISCONNECT LAW
// ────────────────────────────────────────────────────────────────────
// Before this file, onDisconnect() appeared ZERO times in the Empire.
// Every presence node in every title was swept by PEERS — clients
// deleting each other's stale rows on a 15s TTL. That works only while
// somebody else is standing in the same room, which is exactly the
// case a friends list does not have: nobody is standing in your
// friend's room to sweep them, so they would read "online" forever.
//
//   onDisconnect(ref).remove() is the server's promise, kept even when
//   the tab is closed, the laptop lid drops, or the wifi dies.
//
// The 30s heartbeat below is a BACKSTOP, not the mechanism — it covers
// the one case onDisconnect misses (a socket the server still believes
// is alive). Read side treats anything older than PRESENCE_STALE_MS as
// offline regardless of the node existing.
// ════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  ref, get, set, update, remove, onValue, onDisconnect,
  // THE SERVER'S CLOCK — see the `ts` note in invite(). A device clock
  // more than five seconds fast had every invite it sent refused.
  serverTimestamp,
} from "firebase/database";
import { db } from "./firebase";
import { markCode } from "./markData"; // moved to top — import/first (ESLint)

export const IE_SOCIAL_VERSION = "1.1";   // 1.1 (2026-10-16): servers — see THE SERVER SIGNAL

// ── DIALS ───────────────────────────────────────────────────────────
export const PRESENCE_BEAT_MS  = 30000;   // ⟵ DIAL  backstop heartbeat
export const PRESENCE_STALE_MS = 120000;  // ⟵ DIAL  read-side offline cutoff
export const INVITE_TTL_MS     = 120000;  // ⟵ DIAL  an invite goes cold
export const FRIEND_CAP        = 200;     // ⟵ DIAL  sanity ceiling, not a law
export const NAME_MAX          = 24;      // ⟵ LAW, not a dial — rules.json enforces it

// ── THE SERVER'S CLOCK, READ-SIDE ──────────────────────────────────
// invite() stamps `ts` with serverTimestamp() (see the note there), and
// the presence heartbeat now does the same — but every READER of those
// stamps used to compare against the device's own Date.now(). The
// sender's end of the skew window was closed; the receiver's never was:
// a phone two minutes fast reaped every invite the instant it landed,
// and read every friend as offline. `.info/serverTimeOffset` is the
// SDK's own estimate of (server − local); it is free, it is one
// listener for the whole app, and serverNow() is what every TTL and
// staleness check below measures against.
let _serverOffset = 0;
let _offsetArmed  = false;
function armServerClock() {
  if (_offsetArmed) return;
  _offsetArmed = true;
  try {
    onValue(ref(db, ".info/serverTimeOffset"), (s) => {
      const v = s.val();
      _serverOffset = typeof v === "number" && Number.isFinite(v) ? v : 0;
    });
  } catch { /* offline builds: fall back to the local clock */ }
}
// Self-arming (2026-10-16): Terror's Plaza reads this with no friends hook
// of its own mounted yet; the first read must not be the device's bare clock
// forever. Arming is idempotent and costs one listener per app.
export const serverNow = () => { armServerClock(); return Date.now() + _serverOffset; };

// ⚠ THE NAME CLAMP. rules.json caps every stored name at NAME_MAX, and
// no title caps its signup field. A player with a 25-character name —
// which a Google display name routinely is — had every presence write
// and every friend write REFUSED, silently, by a .catch(() => {}).
// They would be permanently invisible and permanently un-addable, in
// every game, with nothing in any console to say why.
//
// Clamp at every site that writes a name. Never trust the caller.
export const clipName = (s) => String(s || "Unclaimed").slice(0, NAME_MAX);

// ════════════════════════════════════════════════════════════════════
// THE GAME REGISTRY
// ════════════════════════════════════════════════════════════════════
// A DATA TABLE, NOT AN ENUM (THE EXCEPTION LAW). A new title joins the
// Empire's social layer by adding a row here and re-vendoring — no
// switch statement anywhere reads these ids, no component branches on
// them. `url: null` means "known, not yet reachable"; a friend seen in
// a game with no url still shows a location pill, they just cannot be
// travelled to yet.
// ⚠ SETTING A url HERE IS HALF THE JOB. rules.json validates the invite
// url against an explicit domain allowlist, because an invite carries
// the player's uid as a query param and an unchecked url would let a
// friend hand you a button that ships your identity to their domain.
// Give a title a url here and it will build invites happily — and every
// one of them will be refused, reported as "Could not send that
// invite." MIRROR THE DOMAIN INTO rules.json > invites > url > .validate
// IN THE SAME COMMIT.
export const IE_GAMES = {
  pier:         { label: "The Pier",       glyph: "❖", color: "#5fa79f" },
  terror:       { label: "Terror TCG",     glyph: "◈", color: "#7c3aed" },
  casino:       { label: "Snake Eyes",     glyph: "⬖", color: "#d4a03c" },
  annihilation: { label: "Annihilation",   glyph: "✜", color: "#c01427" },
  gruel:        { label: "Gruel",          glyph: "◇", color: "#8b8496" },
  sol:          { label: "Sol Survivor",   glyph: "♥", color: "#38bdf8" },
  lyraea:       { label: "Lyraea",         glyph: "❋", color: "#c2557f" },
  bebecitos:    { label: "Bebecitos",      glyph: "⬗", color: "#6fa35f" },
  perry:        { label: "Perry",          glyph: "⧫", color: "#b0607f" },
  swat:         { label: "S'wat",          glyph: "✧", color: "#c9a84c" },
  dtd:          { label: "Dusk till Dawn", glyph: "◆", color: "#c0724a" },
  fractured:    { label: "Fractured",      glyph: "◇", color: "#7f7ac0" },
  tilt:         { label: "Tilt",           glyph: "◐", color: "#4f9e9a" },
  jbb:          { label: "J'aeonxi's Bash",  glyph: "✧", color: "#9b6fc0" },
  // ⭐ 2026-10-16 — a friend standing in either of these read "Somewhere".
  hooked:       { label: "Hooked",         glyph: "◒", color: "#d9a83f" },
  shatterproof: { label: "Shatterproof",   glyph: "⬙", color: "#7ce7ff" },
};

// URLs live apart from the display row on purpose: a title's label and
// glyph are safe to add any time, but a url is half a change — see the
// allowlist warning above.
export const IE_GAME_URLS = {
  pier:   "https://unstable-pierre.pages.dev",
  terror: "https://terror-card-game.pages.dev",
  casino: "https://snake-eyes.pages.dev",
  // ⭐ 2026-09-05 — the mirror had drifted in the direction the warning
  // above does NOT cover. rules.json's invite `url` .validate already
  // allowlists https://lyraea.pages.dev, so the permission existed and
  // the row to use it did not: every invite to Lyraea short-circuited on
  // "Lyraea can't be travelled to yet," and a friend standing in it
  // showed a JOIN button that always refused. Adding the row is the
  // whole fix, and it is safe precisely BECAUSE the domain is already
  // in the deployed allowlist — the other eleven registered titles are
  // deliberately still absent, because inventing a url for a title that
  // is not on that allowlist turns a clear "not open yet" into a silent
  // "Could not send that invite."
  lyraea: "https://lyraea.pages.dev",
};
Object.keys(IE_GAMES).forEach(k => { IE_GAMES[k].url = IE_GAME_URLS[k] || null; });

// ── THE DEV DOORS (2026-10-16) ───────────────────────────────────────
// On caP'n's PC every title runs on its own fixed port (mass_transfer/
// PORTS.md). While THIS page is on localhost, the place a friend should
// be sent is that port, not the deployed site — otherwise "join my
// server" throws you off your own build and onto pages.dev. Deployed,
// none of this exists. ⚠ This is presence/travel only: invite() still
// uses the registry url, because rules.json allowlists invite urls and
// a localhost invite would be refused.
// ⚠ KEEP IN STEP WITH PORTS.md. A wrong port here is a JOIN that lands
// on the wrong game.
export const IE_DEV_PORTS = {
  pier: 3032, terror: 3013, casino: 3028, annihilation: 3011, gruel: 3017,
  sol: 3029, lyraea: 3024, bebecitos: 3012, perry: 3026, swat: 3030,
  fractured: 3016, tilt: 3031, jbb: 3022, hooked: 3020, shatterproof: 3036,
};
export const ON_LOCALHOST = typeof window !== "undefined" && !!window.location &&
  /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
// Where a player in `game` can be reached from here. null = nowhere yet.
export const gameUrl = (game) => {
  if (!game) return null;
  if (ON_LOCALHOST && IE_DEV_PORTS[game]) return `http://${window.location.hostname}:${IE_DEV_PORTS[game]}`;
  return (IE_GAMES[game] && IE_GAMES[game].url) || null;
};

// ⚠ THE TRAVEL GATE. travel() puts the player's uid on the url it is
// handed, and a presence row's url is written by the FRIEND — rules.json
// checks that it is a string and nothing more. Before this gate a
// tampered row could hand you a GO button that shipped your uid to any
// domain at all. Only the Empire's own doors (and, on localhost, the
// dev doors) are followed; anything else falls back to the registry.
const TRUSTED_ORIGINS = new Set(Object.values(IE_GAME_URLS).map((u) => { try { return new URL(u).origin; } catch (e) { return null; } }).filter(Boolean));
export function isTrustedTravelUrl(url) {
  if (!url || typeof url !== "string") return false;
  try {
    const u = new URL(url);
    if (TRUSTED_ORIGINS.has(u.origin)) return true;
    return ON_LOCALHOST && /^(localhost|127\.0\.0\.1)$/.test(u.hostname) && u.protocol === "http:";
  } catch (e) { return false; }
}

// ════════════════════════════════════════════════════════════════════
// THE SERVER SIGNAL (2026-10-16)
// ════════════════════════════════════════════════════════════════════
// caP'n: "if you're partied up and you walk into Hooked, your friend can
// walk in right after … or of course you can click on a friend's
// profile and simply join their server."
//
// A SERVER is one copy of a game's world. "1" is the public one every
// title starts on; anything else is a solo server somebody opened. The
// rules of WHICH server you land on live in ieServers.js — this file
// only carries the two facts the friends list needs:
//
//   · which server I am on      → setPresenceServer(sid). Rides on the
//     presence url as ?server=, because presence/$uid whitelists its
//     keys and a new `server` field would refuse the whole row (the
//     same two-channel trick invite() uses for match codes). A friend's
//     GO therefore lands on the server, not just the game.
//   · how to join one in place  → setServerJoinHandler(fn). A friend on
//     another server of the game you are ALREADY in is a swap, not a
//     page load. No handler = the JOIN travels instead, and the
//     ?server= on arrival does the rest.
export const PUBLIC_SERVER = "1";
export const cleanServerId = (v) => {
  const s = v == null ? "" : String(v).replace(/[^0-9A-Za-z_-]/g, "").slice(0, 24);
  return s || null;
};
export function withServer(url, sid) {
  const id = cleanServerId(sid);
  if (!url || !id) return url || null;
  try {
    const abs = /^https?:/i.test(url);
    const u = new URL(url, "https://x.invalid");
    u.searchParams.set("server", id);
    return abs ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
  } catch (e) { return `${url}${url.includes("?") ? "&" : "?"}server=${id}`; }
}
export function serverOfUrl(url) {
  if (!url) return null;
  const m = /[?&]server=([0-9A-Za-z_-]{1,24})/.exec(String(url));
  return m ? m[1] : null;
}
// The server an invite carries (null = "just the game").
export const serverCodeOf = (inv) => serverOfUrl(inv && inv.url);

let _presenceServer = null;
const _presenceServerSubs = new Set();
export function setPresenceServer(sid) {
  const next = cleanServerId(sid);
  if (next === _presenceServer) return;
  _presenceServer = next;
  _presenceServerSubs.forEach((f) => { try { f(next); } catch (e) {} });
}
export const getPresenceServer = () => _presenceServer;
export function usePresenceServer() {
  const [s, setS] = useState(_presenceServer);
  useEffect(() => {
    _presenceServerSubs.add(setS);
    setS(_presenceServer);
    return () => { _presenceServerSubs.delete(setS); };
  }, []);
  return s;
}

let _serverJoiner = null;
export function setServerJoinHandler(fn) { _serverJoiner = typeof fn === "function" ? fn : null; }
export function serverJoinHandler() { return _serverJoiner; }

export const UNKNOWN_GAME = { label: "Somewhere", glyph: "◆", color: "#59536a", url: null };
export const gameMeta = (id) => (id && IE_GAMES[id]) ? { id, ...IE_GAMES[id] } : { id: id || null, ...UNKNOWN_GAME };

// ════════════════════════════════════════════════════════════════════
// PATHS — every social node in one place, nothing hardcoded at a call site
// ════════════════════════════════════════════════════════════════════
export const friendsPath    = (uid)        => `profiles/${uid}/friends`;
export const friendEdgePath = (uid, fUid)  => `profiles/${uid}/friends/${fUid}`;
export const presencePath   = (uid)        => `presence/${uid}`;
export const invitesPath    = (uid)        => `invites/${uid}`;
export const invitePath     = (to, from)   => `invites/${to}/${from}`;
export const markIndexPath  = (codeKey)    => `markIndex/${codeKey}`;

// ════════════════════════════════════════════════════════════════════
// THE FRIEND CODE — it is THE MARK CODE. There is only one.
// ════════════════════════════════════════════════════════════════════
// The first draft of this file DUPLICATED markData's markCode() here,
// reasoning that markData existed in only two apps. That was solving
// the wrong problem: the answer is to vendor markData everywhere (it
// needs React and Firebase and nothing else), not to keep a second
// copy of a hash whose output is printed on player identity cards. Two
// copies of that function is a schism waiting for someone to "improve"
// one of them, and the day they diverge every code in circulation
// breaks with no error to explain it.
//
// So the friend code IS the Mark code, imported, not re-derived. The
// code a player reads off their Mark and the code they type into ADD
// are the same eight characters because they are the same function.
export { markCode as friendCode, markSeal } from "./markData";
// (import moved to the top of the file — import/first, ESLint)

// The index key: the eight payload characters, no prefix, no dashes.
export const codeKey = (uid) => markCode(uid).replace(/^MK-/, "").replace(/-/g, "");

// Deliberately forgiving on input — players read these off a phone
// screen — and strict on output. Crockford drops I, L, O and U on
// purpose, so a player who typed one of those meant its lookalike.
export const normalizeCode = (raw) => {
  if (!raw) return null;
  const s = String(raw).toUpperCase()
    .replace(/^MK[-\s]?/, "")
    .replace(/[^0-9A-Z]/g, "")
    .replace(/O/g, "0").replace(/I/g, "1").replace(/L/g, "1").replace(/U/g, "V");
  return s.length === 8 ? s : null;
};

export const prettyCode = (key) => key ? `MK-${key.slice(0, 4)}-${key.slice(4)}` : "";

// ════════════════════════════════════════════════════════════════════
// useIEPresence — "I am here, and here is where here is"
// ════════════════════════════════════════════════════════════════════
// Mount ONCE per title, as high as the uid is known. `game` is a key of
// IE_GAMES. `zone` is free text for the room inside that game ("The
// Pier", "Blackjack Table 2", "Endless Depths") — display only, no
// system reads it.
//
// `url` is where a friend should be sent to reach you. Default is the
// registry row for the game, which is right almost always; pass it
// explicitly for a title served from somewhere else.
export function useIEPresence({ uid, name, game, zone = null, url = undefined, enabled = true }) {
  armServerClock();

  // ⚠ THIS EFFECT HAS BEEN WRONG TWICE. Both mistakes are recorded here
  // because both look like the obvious thing to do.
  //
  //   TRY 1 — one effect keyed on everything, cleanup calls remove().
  //     A player walking between rooms (a `zone` change) blinked OFFLINE
  //     to every friend for a full round-trip, at every doorway.
  //     The heartbeat was update({ts}), and onDisconnect fires on any
  //     socket blip — a wifi hiccup, a phone locking. update() on a
  //     deleted path RECREATES it with ts and nothing else, so the
  //     player then read as online in "Somewhere" with no game, no zone
  //     and no way to travel to them, until they reloaded. The exact
  //     "online forever" failure THE DISCONNECT LAW exists to prevent,
  //     reintroduced by the backstop meant to catch it.
  //
  //   TRY 2 — two effects, one for the promise and one for the content.
  //     That fixed the blink but broke the ordering: the content effect
  //     fired its set() with no relationship to the arm, so the row
  //     could land BEFORE onDisconnect was registered. A client dying
  //     in that window is online forever with no peer to sweep it,
  //     because nobody else is ever standing in presence/.
  //
  // TRY 3, below, is one effect keyed on uid alone — so no content
  // change can ever remove the row — with the payload in a ref that is
  // written from an EFFECT, never during render, and every write going
  // through the same armed beat() so the ordering is structural rather
  // than a comment asking the next editor to be careful.

  const payloadRef = useRef({});
  const beatRef    = useRef(null);

  // The server rides on the url (see THE SERVER SIGNAL); null until a
  // title with servers says where it is, which leaves every other title
  // writing exactly the row it always wrote.
  const server = usePresenceServer();
  const payload = useMemo(() => ({
    name: clipName(name),
    game: game || null,
    zone: zone || null,
    url:  withServer(url !== undefined ? url : gameUrl(game), server),
  }), [name, game, zone, url, server]);

  // (1) the promise, the first write, the heartbeat, the sweep — uid only
  useEffect(() => {
    if (!uid || !enabled) { beatRef.current = null; return; }
    const meRef = ref(db, presencePath(uid));
    let cancelled = false;

    // Always a FULL set, never update({ts}): a row resurrected after a
    // blip must come back complete or it comes back useless.
    const beat = () => {
      if (cancelled) return;
      // serverTimestamp(), not Date.now(): readers measure staleness on
      // the server clock (serverNow) and a fast device must not read as
      // "online from the future" nor a slow one as already gone.
      set(meRef, { ...payloadRef.current, ts: serverTimestamp() }).catch(() => {});
    };

    // ORDER IS ENFORCED HERE AND NOWHERE ELSE. beatRef is published only
    // inside the arm's callback, so effect (2) below physically cannot
    // write before onDisconnect is registered — it has nothing to call.
    const arm = () => { if (!cancelled) { beatRef.current = beat; beat(); } };
    onDisconnect(meRef).remove().then(arm).catch(arm);

    const timer = setInterval(beat, PRESENCE_BEAT_MS);
    return () => {
      cancelled = true;
      beatRef.current = null;
      clearInterval(timer);
      onDisconnect(meRef).cancel().catch(() => {});
      remove(meRef).catch(() => {});
    };
  }, [uid, enabled]);

  // (2) content changes — republished, never removed. A no-op until
  // effect (1) has armed, by construction.
  useEffect(() => {
    payloadRef.current = payload;
    if (beatRef.current) beatRef.current();
  }, [payload]);
}

// ════════════════════════════════════════════════════════════════════
// useMarkIndex — publish your own code so friends can find you by it
// ════════════════════════════════════════════════════════════════════
// One write, once, on boot, and only when the row is missing or wrong.
// Not a hot path and never re-written on re-render.
export function useMarkIndex(uid) {
  useEffect(() => {
    if (!uid) return;
    const k = codeKey(uid);
    const r = ref(db, markIndexPath(k));
    get(r).then(snap => {
      if (snap.val() !== uid) set(r, uid).catch(() => {});
    }).catch(() => {});
  }, [uid]);
}

// ════════════════════════════════════════════════════════════════════
// useFriends — the whole social graph, one hook
// ════════════════════════════════════════════════════════════════════
// Returns the roster already sorted the way a player wants to read it:
// online first, then alphabetical. Requests are split out so the UI
// never has to filter by status string at a call site.
export function useFriends({ uid, name, game, enabled = true }) {
  armServerClock();
  const [edges,    setEdges]    = useState({});   // friendUid -> edge
  const [presence, setPresence] = useState({});   // friendUid -> presence row
  const [invites,  setInvites]  = useState([]);   // incoming travel invites
  const [busy,     setBusy]     = useState(false);
  const [error,    setError]    = useState(null);

  const myName = clipName(name);

  // ── the edge list ────────────────────────────────────────────────
  useEffect(() => {
    if (!uid || !enabled) { setEdges({}); return; }
    return onValue(ref(db, friendsPath(uid)), snap => {
      setEdges(snap.val() || {});
    }, () => setEdges({}));
  }, [uid, enabled]);

  // ── one presence listener per friend, added and torn down as the
  //    list changes. Deliberately NOT one listener on all of
  //    presence/ — that would stream every player in the Empire to
  //    every client, and it grows with the playerbase instead of with
  //    the friend count.
  const watched = useRef(new Map());
  // ACCEPTED ONLY. A pending edge gets no presence listener: rules.json
  // grants presence/$uid read solely to an accepted friend, so watching
  // a pending request opens a subscription that is refused on arrival —
  // a PERMISSION_DENIED per pending request, on every boot, buying
  // nothing, because a pending row can never render as online anyway.
  const edgeKeys = useMemo(
    () => Object.keys(edges).filter(k => edges[k] && edges[k].status === "accepted"),
    [edges]
  );

  useEffect(() => {
    // Not a bare return: `enabled` goes false on sign-out, and leaving
    // every listener attached means they keep firing setPresence for a
    // roster this session no longer owns — and re-enabling under a
    // different uid would subscribe on top of the old set.
    const want = new Set(enabled ? edgeKeys : []);

    for (const [fUid, off] of watched.current) {
      if (!want.has(fUid)) {
        off();
        watched.current.delete(fUid);
        setPresence(p => { const n = { ...p }; delete n[fUid]; return n; });
      }
    }
    for (const fUid of want) {
      if (watched.current.has(fUid)) continue;
      const off = onValue(ref(db, presencePath(fUid)), snap => {
        setPresence(p => ({ ...p, [fUid]: snap.val() || null }));
      }, () => setPresence(p => ({ ...p, [fUid]: null })));
      watched.current.set(fUid, off);
    }
  }, [edgeKeys, enabled]);

  // Unmount sweep. Separate effect with an empty dep array on purpose:
  // folded into the one above it would tear down every listener on
  // each roster change.
  useEffect(() => () => {
    for (const [, off] of watched.current) off();
    watched.current.clear();
  }, []);

  // ── incoming travel invites ──────────────────────────────────────
  // ⭐ THE TTL NOW TICKS (2026-09-05). The expiry was evaluated ONLY
  // inside this onValue callback, and `setInvites` was called nowhere
  // else — so once an invite rendered, nothing re-examined it until
  // another write happened to land under `invites/{uid}`. A two-minute-
  // dead invite kept a live, tappable JOIN button on screen indefinitely:
  // exactly the stale-invite state INVITE_TTL_MS exists to prevent. The
  // raw rows are held now and the pruning is a memo over the same 15s
  // `tick` that already drives the roster's online-ness, so an invite
  // ages off the screen on its own within one tick of going cold.
  const [inviteRaw, setInviteRaw] = useState({});
  useEffect(() => {
    if (!uid || !enabled) { setInviteRaw({}); return; }
    return onValue(ref(db, invitesPath(uid)), snap => {
      setInviteRaw(snap.val() || {});
    }, () => setInviteRaw({}));
  }, [uid, enabled]);

  // ── the derived roster ───────────────────────────────────────────
  // A ticking clock, not a bare Date.now() in the memo. Online-ness is
  // a function of TIME, and a friend who closes their laptop produces
  // no Firebase event at all — without a tick the roster would keep
  // showing them lit until something unrelated re-rendered the panel.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const t = setInterval(() => setTick(n => n + 1), 15000);
    return () => clearInterval(t);
  }, [enabled]);

  const isOnline = useCallback((p) => !!(p && p.ts && (serverNow() - p.ts) < PRESENCE_STALE_MS), []);

  // Pruned on every tick, and the reaping write rides along. Deleting a
  // cold row is still best-effort and still tolerant of a refusal — the
  // display no longer depends on the delete succeeding, which is the
  // difference between "tidy" and "correct".
  useEffect(() => {
    const now = serverNow();   // ts is a serverTimestamp — compare like for like
    const live = [];
    Object.entries(inviteRaw).forEach(([fromUid, inv]) => {
      if (!inv || typeof inv !== "object") return;
      if (now - (inv.ts || 0) > INVITE_TTL_MS) {
        if (uid) remove(ref(db, invitePath(uid, fromUid))).catch(() => {});
        return;
      }
      live.push({ ...inv, fromUid });
    });
    live.sort((a, b) => (b.ts || 0) - (a.ts || 0));
    setInvites(live);
  }, [inviteRaw, tick, uid]);

  const rows = useMemo(() => {
    const now = serverNow();
    return Object.entries(edges)
      .filter(([, e]) => e && typeof e === "object")
      .map(([fUid, e]) => {
        const p = presence[fUid] || null;
        const online = !!(p && p.ts && (now - p.ts) < PRESENCE_STALE_MS);
        return {
          uid:    fUid,
          name:   (online && p.name) || e.name || "Unclaimed",
          status: e.status || "accepted",
          addedAt: e.addedAt || 0,
          code:   markCode(fUid),
          online,
          game:   online ? gameMeta(p.game) : null,
          zone:   online ? (p.zone || null) : null,
          server: online ? serverOfUrl(p.url) : null,
          // THE TRAVEL GATE — a row's url is the friend's to write.
          travelUrl: online
            ? (isTrustedTravelUrl(p.url) ? p.url : withServer(gameUrl(p.game), serverOfUrl(p.url)))
            : null,
        };
      });
  }, [edges, presence, tick]);

  const friends    = useMemo(() => rows.filter(r => r.status === "accepted")
    .sort((a, b) => (b.online - a.online) || a.name.localeCompare(b.name)), [rows]);
  const requestsIn  = useMemo(() => rows.filter(r => r.status === "in"),  [rows]);
  const requestsOut = useMemo(() => rows.filter(r => r.status === "out"), [rows]);
  const blocked     = useMemo(() => rows.filter(r => r.status === "blocked"), [rows]);
  const onlineCount = useMemo(() => friends.filter(r => r.online).length, [friends]);

  // ════════════════════════════════════════════════════════════════
  // MUTATIONS
  // ════════════════════════════════════════════════════════════════
  // Every one of these writes at most two nodes: a row on my own list,
  // and the row keyed by my uid on theirs. Nothing else is reachable.

  const accept = useCallback(async (fUid) => {
    if (!uid || !fUid) return { ok: false, error: "Not signed in." };
    setBusy(true);
    try {
      const stamp = Date.now();
      const theirName = clipName(edges[fUid]?.name);
      // Both edges, one transaction — same reasoning as addByUid.
      //
      // ⚠ WHOLE OBJECTS, NOT FIELD PATHS (2026-09-06). This used to write
      // six single-field paths (…/status, …/name, …/addedAt on each
      // edge), on the belief that a whole-object set would "omit" the
      // status the rule reads. It is the other way round. RTDB evaluates
      // every path of a multi-path update on its own, and at the far
      // edge the rule is:
      //   data.child('status').val() === 'out'
      //   && newData.child('status').val() === 'accepted'
      // For the `/name` and `/addedAt` paths `newData` is the existing
      // row plus that ONE field — so newData.status is still "out", the
      // branch fails, the whole update is refused, and no friendship
      // could ever be completed ("Could not accept."). A whole-object
      // write carries status:"accepted" on the same path the rule
      // checks, and `data` is still the old "out" row. Both edges are
      // written as objects for the same reason addByUid does.
      await update(ref(db), {
        [friendEdgePath(uid,  fUid)]: { status: "accepted", name: theirName, addedAt: stamp },
        [friendEdgePath(fUid, uid )]: { status: "accepted", name: myName,    addedAt: stamp },
      });
      setBusy(false);
      return { ok: true };
    } catch {
      setBusy(false);
      return { ok: false, error: "Could not accept." };
    }
  }, [uid, myName, edges]);

  const addByUid = useCallback(async (fUid, fName) => {
    setError(null);
    if (!uid)          return { ok: false, error: "Not signed in." };
    if (!fUid)         return { ok: false, error: "No such player." };
    if (fUid === uid)  return { ok: false, error: "That's your own code, partner." };

    const mine = edges[fUid];
    if (mine?.status === "accepted") return { ok: false, error: "Already friends." };
    if (mine?.status === "blocked")  return { ok: false, error: "You have them blocked." };
    if (mine?.status === "out")      return { ok: false, error: "Request already sent." };
    // ⚠ ACCEPTED ROWS ONLY. This counted every edge — including `blocked`,
    // `in` and `out` — so blocking 200 people permanently locked you out of
    // ever adding a friend again, with a message about a friend limit.
    if (Object.values(edges).filter((e) => e && e.status === "accepted").length >= FRIEND_CAP) return { ok: false, error: "Friends list is full." };

    // THE MUTUAL ACCEPT. If they already asked me, adding them back is
    // the accept — no player should have to find the request in a
    // separate list to complete a handshake they just initiated from
    // the other side.
    if (mine?.status === "in") return accept(fUid);

    setBusy(true);
    try {
      let theirName = fName;
      if (!theirName) {
        const s = await get(ref(db, `profiles/${fUid}`));
        const prof = s.val();
        if (!prof) { setBusy(false); return { ok: false, error: "No such player." }; }
        theirName = prof.name || prof.displayName || "Unclaimed";
      }
      const stamp = Date.now();
      // ⚠ ONE MULTI-PATH UPDATE, NOT TWO AWAITS. A friendship is two
      // edges and they must land together. Two sequential writes can
      // leave a HALF-FRIENDSHIP: my list says "waiting on them" for a
      // request that was never delivered, and addByUid then refuses to
      // retry forever because it sees status "out" and early-returns.
      // RTDB applies a root update() as a single transaction — all
      // paths or none — so the split state is simply unreachable.
      await update(ref(db), {
        [friendEdgePath(uid,  fUid)]: { status: "out", name: clipName(theirName), addedAt: stamp },
        [friendEdgePath(fUid, uid )]: { status: "in",  name: myName,              addedAt: stamp },
      });
      setBusy(false);
      return { ok: true, name: theirName };
    } catch (e) {
      setBusy(false);
      const msg = e?.code === "PERMISSION_DENIED"
        ? "The Empire refused that write — rules may not be deployed."
        : "Could not send that request.";
      setError(msg);
      return { ok: false, error: msg };
    }
  }, [uid, myName, edges, accept]);

  const addByCode = useCallback(async (raw) => {
    const key = normalizeCode(raw);
    if (!key) return { ok: false, error: "A code looks like MK-XXXX-XXXX." };
    if (key === codeKey(uid)) return { ok: false, error: "That's your own code, partner." };
    try {
      const snap = await get(ref(db, markIndexPath(key)));
      const fUid = snap.val();
      if (!fUid || typeof fUid !== "string") return { ok: false, error: "Nobody carries that code." };
      // ⚠ THE SQUAT DEFENCE. markIndex rows can only be written with
      // your own uid as the VALUE — but the rules language cannot hash,
      // so it can never check that the KEY is your own code. Nothing
      // stops an attacker computing codeKey(victim) and claiming that
      // row first, permanently redirecting the victim's printed Mark
      // code to themselves. Re-deriving the code from the uid we got
      // back makes the squat inert: a forged row simply fails to match
      // and reads as "nobody carries that code".
      if (codeKey(fUid) !== key) return { ok: false, error: "Nobody carries that code." };
      return addByUid(fUid, null);
    } catch {
      return { ok: false, error: "Could not reach the Empire." };
    }
  }, [uid, addByUid]);

  // Decline and unfriend are the same two writes — remove both edges.
  // The asymmetry people expect (a decline being quieter than an
  // unfriend) is a UI wording difference, not a data one.
  const removeFriend = useCallback(async (fUid) => {
    if (!uid || !fUid) return { ok: false };
    setBusy(true);
    try {
      await update(ref(db), {
        [friendEdgePath(uid,  fUid)]: null,
        [friendEdgePath(fUid, uid )]: null,
        // Retract any invite still sitting on their screen. Unfriending
        // someone while a live "come over here" toast of yours is still
        // showing is not a state anyone should be able to reach.
        [invitePath(fUid, uid)]:      null,
      });
      setBusy(false);
      return { ok: true };
    } catch { setBusy(false); return { ok: false, error: "Could not do that." }; }
  }, [uid]);

  const decline = removeFriend;

  // BLOCK IS ONE-SIDED, exactly as canon specifies. I keep a blocked
  // row so they can never re-request; I delete myself from their list
  // so they cannot see me, cannot invite me, and get no notification
  // that anything happened.
  const block = useCallback(async (fUid, fName) => {
    if (!uid || !fUid) return { ok: false };
    setBusy(true);
    try {
      // One transaction, or the block half-lands: I get the blocked
      // row while they keep seeing me and can still invite me.
      await update(ref(db), {
        [friendEdgePath(uid, fUid)]: {
          status: "blocked",
          name: clipName(fName || edges[fUid]?.name),
          addedAt: Date.now(),
        },
        [friendEdgePath(fUid, uid)]: null,
        // Both directions. Clearing only mine left THEIR "come over
        // here" toast sitting on my screen after I blocked them.
        [invitePath(fUid, uid)]:     null,
        [invitePath(uid, fUid)]:     null,
      });
      setBusy(false);
      return { ok: true };
    } catch { setBusy(false); return { ok: false, error: "Could not block." }; }
  }, [uid, edges]);

  const unblock = useCallback(async (fUid) => {
    if (!uid || !fUid) return { ok: false };
    try { await remove(ref(db, friendEdgePath(uid, fUid))); return { ok: true }; }
    catch { return { ok: false }; }
  }, [uid]);

  // ── COME OVER HERE ───────────────────────────────────────────────
  // Invites carry the URL rather than only a game id, so the receiver
  // can travel without owning a copy of the registry — a title added
  // to the Empire after this build still reaches every older client.
  // ⭐ AN INVITE CARRIES A SEAT NOW (2026-09-05).
  //
  // Before this, "invite" meant a URL and nothing else. The accept handler
  // was one line — `go(inv.url, ...)` — so tapping JOIN navigated the
  // friend to the game's front door and stopped. It never referenced a
  // match, never carried a code, never called a join. In Terror the friend
  // landed on the Plaza and the two of them still had to read a 4-digit
  // code to each other out loud. The toast said "wants you in Terror TCG",
  // which was honest; the player heard "join my duel", which was not
  // deliverable. `opts.matchCode` is the whole difference.
  //
  // ⚠ AND IT RIDES TWO CHANNELS, ON PURPOSE, BECAUSE OF THE RULES.
  // `invites/$to/$from` ends in `"$extra": { ".validate": false }` — a
  // whitelist on KEY NAMES. A new `matchCode` field would be refused by
  // the deployed rules and the whole invite would die in the catch below
  // with "Could not send that invite." So the code rides inside two fields
  // that already exist and are already permitted:
  //   · `note`  — a free string ≤80, as "match:4711". This is the one
  //               that works on localhost, where the allowlisted
  //               production URL is not where the player actually is.
  //   · `url`   — validated by PREFIX only, so `?match=4711` on the end
  //               passes untouched. This is the one that survives a
  //               cross-title trip, because the destination reads it
  //               straight off the query string on arrival.
  // rules.json gains a real `matchCode` field in the same pass, for
  // whenever caP'n deploys — `matchCodeOf` below reads all three, newest
  // first, so a deployed field simply starts winning and nothing breaks
  // in the meantime.
  const invite = useCallback(async (fUid, opts = {}) => {
    if (!uid || !fUid) return { ok: false, error: "Not signed in." };
    const gameId = opts.gameId || game || null;
    const meta   = gameMeta(gameId);
    const baseUrl = opts.url || meta.url;
    if (!baseUrl) return { ok: false, error: `${meta.label} can't be travelled to yet.` };
    const mc = opts.matchCode ? String(opts.matchCode).replace(/[^0-9A-Za-z]/g, "").slice(0, 8) : null;
    let url = baseUrl;
    if (mc) {
      try {
        const u = new URL(baseUrl, "https://x.invalid");
        u.searchParams.set("match", mc);
        url = baseUrl.startsWith("http") ? u.toString() : `${baseUrl}?match=${mc}`;
      } catch { url = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}match=${mc}`; }
    }
    // ⭐ 2026-10-16 — "come to MY server", not just my game. Same channel
    // as the match code: the url is prefix-validated, so a query param
    // passes the deployed rules untouched.
    const sv = cleanServerId(opts.server);
    if (sv) url = withServer(url, sv);
    const note = mc
      ? `match:${mc}${opts.note ? ` · ${String(opts.note).slice(0, 60)}` : ""}`.slice(0, 80)
      : (opts.note ? String(opts.note).slice(0, 80) : null);
    try {
      await set(ref(db, invitePath(fUid, uid)), {
        fromUid:  uid,
        fromName: myName,   // already clipped
        gameId,
        // Clamped to what rules.json accepts. Unclamped, a long note
        // silently killed the whole invite through the catch below.
        label:    String(opts.label || meta.label).slice(0, 40),
        url,
        partyId:  opts.partyId || null,
        note,
        // ⭐ THE SERVER'S CLOCK, NOT THE SENDER'S (2026-09-05). `ts` was
        // `Date.now()` and rules.json validates it against `now` — the
        // SERVER's millisecond:
        //     newData.val() <= now + 5000 && newData.val() > now - 300000
        // So a sender whose device clock ran more than five seconds fast,
        // or five minutes slow, had EVERY invite refused, surfacing as a
        // bare "Could not send that invite." with nothing to diagnose.
        // And on the read side a recipient whose clock ran two minutes
        // fast silently deleted every invite the instant it arrived,
        // through the TTL reaper below. RTDB resolves this sentinel before
        // rules evaluate, so it validates as a number and the skew window
        // closes on both ends at once.
        ts:       serverTimestamp(),
      });
      return { ok: true, label: meta.label };
    } catch {
      return { ok: false, error: "Could not send that invite." };
    }
  }, [uid, myName, game]);

  const clearInvite = useCallback(async (fromUid) => {
    if (!uid || !fromUid) return;
    await remove(ref(db, invitePath(uid, fromUid))).catch(() => {});
  }, [uid]);

  return {
    // roster
    friends, requestsIn, requestsOut, blocked, onlineCount,
    invites,
    // state
    busy, error, isOnline,
    myCode: markCode(uid),
    // mutations
    addByCode, addByUid, accept, decline, removeFriend, block, unblock,
    invite, clearInvite,
  };
}

// ════════════════════════════════════════════════════════════════════
// travelTo — the one way a player crosses a border
// ════════════════════════════════════════════════════════════════════
// Carries the uid so the destination inherits the session without a
// second login (see ieIdentity.js, THE HANDOFF), and the partyId so a
// party crossing together stays together — the same contract
// PartySystem's withPartyParam() already established.
// ── READING THE SEAT BACK OUT ────────────────────────────────────────
// Three channels, newest first: a real `matchCode` field (present only
// once rules.json is deployed), the `?match=` query param on the url, and
// the `match:CODE` prefix on the note. A client from before this change
// simply finds none of them and behaves exactly as it always did.
export const matchCodeOf = (inv) => {
  if (!inv) return null;
  if (inv.matchCode) return String(inv.matchCode).replace(/[^0-9A-Za-z]/g, "").slice(0, 8) || null;
  if (inv.url) {
    const m = /[?&]match=([0-9A-Za-z]{1,8})/.exec(String(inv.url));
    if (m) return m[1];
  }
  if (inv.note) {
    const m = /^match:([0-9A-Za-z]{1,8})/.exec(String(inv.note));
    if (m) return m[1];
  }
  return null;
};

// The player-facing half of the note, with the machine prefix stripped —
// so a toast never shows "match:4711" to a human.
export const inviteNoteOf = (inv) => {
  const n = inv?.note ? String(inv.note) : "";
  if (!n) return null;
  const stripped = n.replace(/^match:[0-9A-Za-z]{1,8}\s*(·\s*)?/, "");
  return stripped || null;
};

// ── JOIN WITHOUT LEAVING ─────────────────────────────────────────────
// A title registers one function here and an invite to a match IN THE
// GAME YOU ARE ALREADY STANDING IN stops being a page navigation. That
// matters for more than elegance: `url` is domain-allowlisted to the
// production pages.dev, so on localhost — every dev session, and every
// live-QA lap — travelling would throw the player off their own build.
// Same shape as Terror's own setPairingStarter bridge: module-level
// registry, one writer, cleared on unmount.
let _matchJoiner = null;
export function setMatchInviteHandler(fn) { _matchJoiner = typeof fn === "function" ? fn : null; }
export function matchInviteHandler() { return _matchJoiner; }

// ── THE TITLE'S OWN VERBS (2026-09-06) — THE EXCEPTION LAW at work ──
// IEFriends is vendored byte-identical into every title, so it cannot
// know that Terror trades cards and Snake Eyes does not. Two registries
// let a title add what only it has, without a fork of the panel:
//
//   setFriendActions((friend) => [{ label, run(friend) }])
//       extra buttons in a friend row's drawer — Terror registers TRADE.
//   setInviteOpener((inv) => boolean)
//       first look at a tapped invite. Return true = "handled, do not
//       navigate" — Terror opens a trade invite in the Exchange Post
//       the way a match invite is seated in place.
//
// Both are cleared on unmount by the title that set them. A title that
// registers nothing gets exactly the panel it had yesterday.
let _friendActions = null;
export function setFriendActions(fn) { _friendActions = typeof fn === "function" ? fn : null; }
export function friendActionsFor(friend) {
  if (!_friendActions) return [];
  try { return _friendActions(friend) || []; } catch (e) { return []; }
}
let _inviteOpener = null;
export function setInviteOpener(fn) { _inviteOpener = typeof fn === "function" ? fn : null; }
export function inviteOpener() { return _inviteOpener; }

// ── LETTING GO OF THE ROOM ───────────────────────────────────────────
// ⭐ (2026-09-05) Logging out left the presence row behind, and the order
// is the whole reason. Terror's handleLogout called signOut() first; the
// auth listener then unmounted the social layer, so useIEPresence's
// cleanup — which does exactly this — ran ALREADY UNAUTHENTICATED.
// `presence/$uid` requires `auth.uid === $uid`, so the remove() came back
// permission_denied straight into a .catch(() => {}) and the row survived.
// Friends saw a logged-out player as online, in Terror, for up to the full
// 120-second PRESENCE_STALE_MS. Call this BEFORE signOut, while the
// credential that owns the row still exists.
export async function releasePresence(uid) {
  if (!uid) return;
  try {
    const r = ref(db, presencePath(uid));
    await onDisconnect(r).cancel().catch(() => {});
    await remove(r).catch(() => {});
  } catch (e) { /* best effort — the 120s staleness cutoff is the backstop */ }
}

// ── THE BRIDGE ───────────────────────────────────────────────────────
// The social layer is mounted in index.js as a SIBLING of <App/> — the
// thing that makes it one line per title and free of prop threading. The
// cost is that the GAME cannot see the roster, which was fine while the
// only social act was "come to this game" and stopped being fine the
// moment an invite could carry a seat: the screen that knows the match
// code is inside <App/>, and the friend list is outside it.
//
// Same answer this file already gave for suppression (setSocialHidden):
// a module-level signal, not a context, because a context needs a
// provider wrapping <App/> and that is the exact refactor the Dock
// exists to avoid. The layer publishes; anything in the game subscribes.
let _bridge = null;
const _bridgeSubs = new Set();
export function publishSocialBridge(b) {
  _bridge = b;
  _bridgeSubs.forEach((f) => { try { f(b); } catch (e) {} });
}
export function useSocialBridge() {
  const [b, setB] = useState(_bridge);
  useEffect(() => {
    _bridgeSubs.add(setB);
    setB(_bridge);
    return () => { _bridgeSubs.delete(setB); };
  }, []);
  return b;
}

export function travelTo(url, { uid, partyId = null, from = null } = {}) {
  if (!url) return false;
  try {
    const u = new URL(url, window.location.href);
    if (uid)     u.searchParams.set("uid", uid);
    if (partyId) u.searchParams.set("partyId", partyId);
    if (from)    u.searchParams.set("from", from);
    window.location.href = u.toString();
    return true;
  } catch {
    return false;
  }
}

export default useFriends;

// ════════════════════════════════════════════════════════════════════
// THE SUPPRESSION SIGNAL
// ════════════════════════════════════════════════════════════════════
// The social layer is mounted in index.js, a SIBLING of <App/>, which
// is what makes it one line per title and free of prop threading. The
// cost of that is it cannot see the game's modal state — and a friends
// pill floating over the ReadyUpModal's blackout during the 15-second
// synchronized party launch, fully clickable, is exactly the kind of
// thing that makes a good feature feel bolted on.
//
// So a title reaches UP instead: one call from wherever its modal state
// already lives. Deliberately a module-level signal rather than a
// context — a context would need a provider wrapping <App/>, which is
// the refactor the Dock exists to avoid.
//
//   useEffect(() => setSocialHidden(!!readyup || !!portalModal),
//             [readyup, portalModal]);
let _hidden = false;
const _hiddenSubs = new Set();
export function setSocialHidden(v) {
  const next = !!v;
  if (next === _hidden) return;
  _hidden = next;
  _hiddenSubs.forEach(fn => { try { fn(next); } catch {} });
}
export function useSocialHidden() {
  const [h, setH] = useState(_hidden);
  useEffect(() => {
    setH(_hidden);
    _hiddenSubs.add(setH);
    return () => { _hiddenSubs.delete(setH); };
  }, []);
  return h;
}
